import { Children, forwardRef, isValidElement, useCallback, useEffect, useId, useRef } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MenuItem from '@mui/material/MenuItem'
import MuiSelect from '@mui/material/Select'
import styled, { css, keyframes } from 'styled-components'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function useDialogKeyboard(open, onRequestClose, rootRef, initialFocusRef) {
  const onRequestCloseRef = useRef(onRequestClose)

  useEffect(() => {
    onRequestCloseRef.current = onRequestClose
  }, [onRequestClose])

  useEffect(() => {
    if (!open) return undefined

    const previouslyFocused = document.activeElement
    const root = rootRef.current
    const focusableElements = () => Array.from(root?.querySelectorAll(FOCUSABLE_SELECTOR) || [])

    const initialFocus = initialFocusRef.current || focusableElements()[0] || root
    initialFocus?.focus()

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onRequestCloseRef.current?.('escape')
        return
      }
      if (event.key !== 'Tab') return

      const elements = focusableElements()
      if (elements.length === 0) {
        event.preventDefault()
        root?.focus()
        return
      }

      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      previouslyFocused?.focus?.()
    }
  }, [initialFocusRef, open, rootRef])
}

function useCloseRequest(onClose, canClose) {
  return useCallback((reason) => {
    if (canClose?.(reason) === false) return false
    onClose?.(reason)
    return true
  }, [canClose, onClose])
}

export const Page = styled.div`
  width: min(100%, 1240px);
  margin: 0 auto;
  padding: 32px clamp(20px, 4vw, 56px) 48px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    padding: 24px 16px 40px;
  }
`

const PageHeaderRoot = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;

  @media (max-width: 900px) {
    align-items: flex-start;
    flex-direction: column;
    margin-bottom: 20px;
  }
`

const HeaderText = styled.div`
  min-width: 0;
`

const Eyebrow = styled.p`
  margin: 0 0 6px;
  color: ${({ theme }) => theme.colors.muiPrimary};
  font-size: 0.78rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(1.65rem, 3vw, 2.15rem);
  line-height: 1.25;
  letter-spacing: -0.035em;
`

const Description = styled.p`
  max-width: 680px;
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.96rem;
`

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 900px) {
    width: 100%;
  }
`

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <PageHeaderRoot>
      <HeaderText>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <Title>{title}</Title>
        {description && <Description>{description}</Description>}
      </HeaderText>
      {actions && <HeaderActions>{actions}</HeaderActions>}
    </PageHeaderRoot>
  )
}

const buttonVariant = {
  primary: css`
    color: ${({ theme }) => theme.colors.onPrimary};
    border-color: ${({ theme }) => theme.colors.primaryFill};
    background: ${({ theme }) => theme.colors.primaryFill};

    &:hover:not(:disabled) {
      border-color: ${({ theme }) => theme.colors.primaryFillHover};
      background: ${({ theme }) => theme.colors.primaryFillHover};
    }
  `,
  secondary: css`
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.surface};

    &:hover:not(:disabled) {
      border-color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.primarySoft};
    }
  `,
  quiet: css`
    color: ${({ theme }) => theme.colors.textMuted};
    border-color: transparent;
    background: transparent;

    &:hover:not(:disabled) {
      color: ${({ theme }) => theme.colors.text};
      background: ${({ theme }) => theme.colors.primarySoft};
    }
  `,
  danger: css`
    color: ${({ theme }) => theme.colors.danger};
    border-color: rgba(196, 61, 75, 0.24);
    background: rgba(196, 61, 75, 0.08);

    &:hover:not(:disabled) {
      background: rgba(196, 61, 75, 0.14);
    }
  `,
}

export const Button = styled.button.attrs(({ type }) => ({
  type: type || 'button',
}))`
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: 0.92rem;
  font-weight: 680;
  line-height: 1;
  transition: color 150ms ease, border-color 150ms ease, background 150ms ease,
    transform 150ms ease;
  ${({ $variant = 'primary' }) => buttonVariant[$variant] || buttonVariant.primary}

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.48;
  }
`

export const Field = styled.label`
  display: grid;
  gap: 7px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.9rem;
  font-weight: 650;
`

const controlStyle = css`
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.surface};
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
    opacity: 0.72;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primaryAccent};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.muiPrimary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusSoft};
    outline: none;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.textMuted};
    background-color: ${({ theme }) => theme.colors.lightestGray};
  }
`

export const TextInput = styled.input`
  ${controlStyle}
`

const MuiSelectAdapter = forwardRef(function MuiSelectAdapter(
  {
    children,
    className,
    SelectDisplayProps,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...props
  },
  ref,
) {
  const menuItems = Children.map(children, (child) => {
    if (!isValidElement(child)) return child
    if (child.type !== 'option') return child

    const { children: optionLabel, ...optionProps } = child.props
    return (
      <MenuItem key={child.key ?? optionProps.value} {...optionProps}>
        {optionLabel}
      </MenuItem>
    )
  })

  return (
    <MuiSelect
      {...props}
      className={className}
      displayEmpty={props.displayEmpty ?? true}
      inputRef={ref}
      SelectDisplayProps={{
        ...SelectDisplayProps,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
      }}
      MenuProps={{
        PaperProps: { elevation: 4 },
        ...props.MenuProps,
      }}
    >
      {menuItems}
    </MuiSelect>
  )
})

export const SelectInput = styled(MuiSelectAdapter)`
  width: 100%;
  min-height: 44px;
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.surface};

  .MuiSelect-select {
    display: flex;
    min-height: 44px !important;
    align-items: center;
    padding: 8px 38px 8px 12px !important;
    box-sizing: border-box;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }

  &:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.primaryAccent};
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.primary};
    border-width: 1px;
  }

  &.Mui-focused {
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primarySoft};
  }

  && .MuiSelect-icon { color: ${({ theme }) => theme.colors.primary} !important; }
`

export const TextArea = styled.textarea`
  ${controlStyle}
  min-height: 112px;
  resize: vertical;
`

export const Panel = styled.section`
  padding: clamp(18px, 3vw, 28px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.large};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.low};
`

const pulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`

const SkeletonRoot = styled.div`
  display: grid;
  gap: 12px;
`

const SkeletonRow = styled.div`
  height: 58px;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(90deg, #222B39 0%, #2B3546 50%, #222B39 100%)'
    : 'linear-gradient(90deg, #EEF0F4 0%, #F8F9FB 50%, #EEF0F4 100%)'};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const pageSkeletonReveal = keyframes`
  to { opacity: 1; }
`

const PageSkeletonRoot = styled(Page)`
  display: grid;
  gap: 20px;
  opacity: 0;
  animation: ${pageSkeletonReveal} 1ms 120ms forwards;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

const PageSkeletonHeader = styled.div`
  display: grid;
  gap: 10px;
  padding-bottom: 8px;
`

const PageSkeletonBlock = styled.div`
  width: ${({ $width = '100%' }) => $width};
  height: ${({ $height = '16px' }) => $height};
  border-radius: ${({ $radius, theme }) => $radius || theme.radius.small};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(90deg, #222B39 0%, #2B3546 50%, #222B39 100%)'
    : 'linear-gradient(90deg, #EEF0F4 0%, #F8F9FB 50%, #EEF0F4 100%)'};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const PageSkeletonCards = styled.div`
  display: grid;
  gap: 16px;
`

const PageSkeletonCard = styled(PageSkeletonBlock).attrs({
  $height: '176px',
})`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.large};

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    height: 196px;
  }
`

export function SkeletonList({ rows = 5, label = '목록을 불러오는 중' }) {
  return (
    <SkeletonRoot role="status" aria-label={label} aria-live="polite">
      {Array.from({ length: rows }).map((_, index) => (
        <SkeletonRow key={index} aria-hidden="true" />
      ))}
    </SkeletonRoot>
  )
}

export function PageLoadingSkeleton({ label = '화면을 준비하는 중' }) {
  return (
    <PageSkeletonRoot role="status" aria-label={label} aria-live="polite">
      <PageSkeletonHeader aria-hidden="true">
        <PageSkeletonBlock $width="88px" $height="10px" />
        <PageSkeletonBlock $width="min(420px, 72%)" $height="38px" />
        <PageSkeletonBlock $width="min(560px, 88%)" $height="16px" />
      </PageSkeletonHeader>
      <PageSkeletonCards aria-hidden="true">
        <PageSkeletonCard />
        <PageSkeletonCard />
        <PageSkeletonCard />
      </PageSkeletonCards>
    </PageSkeletonRoot>
  )
}

const EmptyStateRoot = styled.div`
  display: grid;
  min-height: 180px;
  place-items: center;
  padding: 32px 20px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.medium};

  strong {
    display: block;
    margin-bottom: 6px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 1rem;
  }

  p {
    margin: 0;
  }
`

export function EmptyState({ title, description, action }) {
  return (
    <EmptyStateRoot>
      <div>
        <strong>{title}</strong>
        {description && <p>{description}</p>}
        {action && <div style={{ marginTop: 16 }}>{action}</div>}
      </div>
    </EmptyStateRoot>
  )
}

const PanelBackdrop = styled.div`
  position: fixed;
  z-index: 80;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(17, 27, 64, 0.34);
  backdrop-filter: blur(2px);
`

const SidePanelRoot = styled.aside`
  display: grid;
  width: min(${({ $size }) => $size === 'wide' ? '640px' : '440px'}, 100%);
  height: 100%;
  grid-template-rows: auto minmax(0, 1fr) auto;
  background: ${({ theme }) => theme.colors.surface};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.medium};
`

const SidePanelHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: ${({ $size }) => $size === 'wide' ? '22px 28px' : '18px 20px'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }
`

const SidePanelHeading = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`

const CloseButton = styled.button`
  display: inline-grid;
  width: 40px;
  height: 40px;
  place-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 0;
  border-radius: 50%;
  background: transparent;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`

const SidePanelBody = styled.div`
  min-height: 0;
  padding: ${({ $size }) => $size === 'wide' ? '28px' : '20px'};
  overflow-y: auto;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) { padding: 20px; }
`

const SidePanelActions = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: ${({ $size }) => $size === 'wide' ? '18px 28px' : '14px 20px'};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) { padding: 14px 20px; }
`

export function SidePanel({
  open,
  title,
  titleAction,
  onClose,
  canClose,
  children,
  actions,
  size = 'default',
}) {
  const titleId = useId()
  const closeButtonRef = useRef(null)
  const rootRef = useRef(null)
  const requestClose = useCloseRequest(onClose, canClose)
  useDialogKeyboard(open, requestClose, rootRef, closeButtonRef)

  if (!open) return null

  return (
    <PanelBackdrop aria-label={`${title} 배경`} onMouseDown={(event) => event.target === event.currentTarget && requestClose('backdrop')}>
      <SidePanelRoot ref={rootRef} $size={size} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex="-1">
        <SidePanelHeader $size={size}>
          <SidePanelHeading>
            <h2 id={titleId}>{title}</h2>
            {titleAction}
          </SidePanelHeading>
          <CloseButton ref={closeButtonRef} type="button" onClick={() => requestClose('close-button')} aria-label={`${title} 닫기`}>
            <CloseRoundedIcon aria-hidden="true" />
          </CloseButton>
        </SidePanelHeader>
        <SidePanelBody $size={size}>{children}</SidePanelBody>
        {actions && (
          <SidePanelActions $size={size}>
            {typeof actions === 'function' ? actions(requestClose) : actions}
          </SidePanelActions>
        )}
      </SidePanelRoot>
    </PanelBackdrop>
  )
}

const DialogBackdrop = styled.div`
  position: fixed;
  z-index: 110;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(17, 27, 64, 0.38);
  backdrop-filter: blur(3px);
`

const dialogWidth = {
  small: '400px',
  default: '520px',
  wide: '720px',
}

const DialogRoot = styled.section`
  display: grid;
  width: min(${({ $size }) => dialogWidth[$size] || dialogWidth.default}, 100%);
  max-height: calc(100dvh - 36px);
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.large};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: 0 24px 70px rgba(17, 27, 64, 0.24);
`

const DialogHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  > div { min-width: 0; flex: 1; }

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.08rem;
    line-height: 1.4;
    letter-spacing: -0.02em;
  }
  p {
    margin: 5px 0 0;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 0.8rem;
    line-height: 1.5;
    overflow-wrap: break-word;
    word-break: keep-all;
  }

  @media (max-width: 480px) { padding: 19px 20px 16px; }
`

const DialogBody = styled.div`
  min-height: 0;
  padding: ${({ $compact }) => $compact ? '20px 24px' : '24px'};
  overflow-y: auto;

  @media (max-width: 480px) { padding: 20px; }
`

const DialogActions = styled.footer`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 480px) {
    padding: 14px 20px 18px;
    button { flex: 1; }
  }
`

export function Dialog({
  open = true,
  title,
  description,
  onClose,
  canClose,
  children,
  actions,
  size = 'default',
  compact = false,
}) {
  const titleId = useId()
  const closeButtonRef = useRef(null)
  const rootRef = useRef(null)
  const requestClose = useCloseRequest(onClose, canClose)
  useDialogKeyboard(open, requestClose, rootRef, closeButtonRef)

  if (!open) return null

  return (
    <DialogBackdrop aria-label={`${title} 배경`} onMouseDown={(event) => event.target === event.currentTarget && requestClose('backdrop')}>
      <DialogRoot ref={rootRef} $size={size} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex="-1">
        <DialogHeader>
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          {onClose && (
            <CloseButton ref={closeButtonRef} type="button" onClick={() => requestClose('close-button')} aria-label={`${title} 닫기`}>
              <CloseRoundedIcon aria-hidden="true" />
            </CloseButton>
          )}
        </DialogHeader>
        <DialogBody $compact={compact}>{children}</DialogBody>
        {actions && <DialogActions>{typeof actions === 'function' ? actions(requestClose) : actions}</DialogActions>}
      </DialogRoot>
    </DialogBackdrop>
  )
}
